import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";

// Microsoft 4-colored squares logo (inline SVG)
function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const ERROR_MESSAGES: Record<string, string> = {
  not_whitelisted:
    "Je e-mailadres is niet geregistreerd. Neem contact op met je beheerder.",
  invalid_token:
    "Deze inloglink is verlopen of al gebruikt. Vraag een nieuwe aan.",
};

export default function Login() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [magicLinkSubmitting, setMagicLinkSubmitting] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState("");
  const [demoMode, setDemoMode] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState<
    { id: number; name: string; email: string; role: string }[]
  >([]);
  const [demoLoading, setDemoLoading] = useState<string | null>(null);
  const { refreshSession } = useAuth();

  // Error from URL param (e.g. ?error=not_whitelisted)
  const urlError = searchParams.get("error") ?? "";
  const urlErrorMessage = ERROR_MESSAGES[urlError] ?? (urlError ? urlError : "");

  // Check demo mode + haal actuele lijst demo-accounts op
  useEffect(() => {
    fetch("/api/auth/demo-status", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d: { demo: boolean; accounts?: { id: number; name: string; email: string; role: string }[] }) => {
        setDemoMode(d.demo);
        if (d.accounts) setDemoAccounts(d.accounts);
      })
      .catch(() => {});
  }, []);

  // Mappa rol → menselijk label
  const roleLabel: Record<string, string> = {
    admin: "Admin",
    kam: "KAM",
    hmp: "HMP",
    partner_admin: "Partner Admin",
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  async function handleDemoLogin(demoEmail: string) {
    setDemoLoading(demoEmail);
    try {
      const res = await fetch("/api/auth/demo-login", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: demoEmail }),
      });
      if (res.ok) {
        await refreshSession();
        navigate("/", { replace: true });
      }
    } catch {
      // ignore
    } finally {
      setDemoLoading(null);
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setMagicLinkError("");
    setMagicLinkSubmitting(true);

    try {
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as Record<string, string>;
        throw new Error(body.error || `Fout: ${res.status}`);
      }

      setMagicLinkSent(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Er is een fout opgetreden.";
      setMagicLinkError(message);
    } finally {
      setMagicLinkSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#0F172A" }}
    >
      {/* Background glow accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #65ADDD 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #65ADDD 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative w-full max-w-md px-4 md:px-0">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Navy header */}
          <div
            className="px-6 py-6 md:px-8 md:py-8 text-center"
            style={{ backgroundColor: "#1B2A4A" }}
          >
            <div className="flex flex-col items-center">
              <img
                src="/logo.png"
                alt="Waves"
                className="h-16 w-auto rounded-lg mb-2"
              />
              <div className="text-sm font-medium mt-0.5" style={{ color: "#65ADDD" }}>
                Partnerportaal
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-6 md:px-8 md:py-8 space-y-6">

            {/* URL error alert */}
            {urlErrorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{urlErrorMessage}</span>
              </div>
            )}

            {/* Primary: Microsoft SSO */}
            <div>
              <button
                type="button"
                onClick={() => { window.location.href = "/api/auth/signin/microsoft"; }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 min-h-[48px] bg-white border border-slate-200 rounded-xl text-slate-800 font-medium text-sm shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-150"
              >
                <MicrosoftLogo />
                Inloggen met Microsoft
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400 font-medium">of</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Fallback: Magic link */}
            {magicLinkSent ? (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-4 flex items-start gap-3">
                <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">Controleer je inbox</p>
                  <p className="text-sm text-green-700 mt-1">
                    Als <span className="font-semibold">{email.trim()}</span> bij ons bekend is,
                    ontvang je binnen enkele minuten een inloglink.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleMagicLink} className="space-y-3">
                <label
                  htmlFor="magic-email"
                  className="block text-sm font-medium text-slate-700"
                >
                  Inloggen met e-maillink
                </label>

                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    />
                    <input
                      id="magic-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="je@e-mailadres.nl"
                      disabled={magicLinkSubmitting}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 placeholder-slate-400"
                      style={{ "--tw-ring-color": "#65ADDD" } as React.CSSProperties}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={magicLinkSubmitting || !email.trim()}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium text-white transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap sm:whitespace-normal"
                    style={{ backgroundColor: "#65ADDD" }}
                    onMouseEnter={(e) => { if (!magicLinkSubmitting) (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#4d9ccc"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#65ADDD"; }}
                  >
                    {magicLinkSubmitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Versturen…
                      </>
                    ) : (
                      "Verstuur inloglink"
                    )}
                  </button>
                </div>

                {magicLinkError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm flex items-start gap-2">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{magicLinkError}</span>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Demo accounts */}
        {demoMode && demoAccounts.length > 0 && (
          <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Demo Accounts</p>
            <div className="space-y-1.5">
              {demoAccounts.map((u) => ({
                label: roleLabel[u.role] ?? u.role,
                name: u.name,
                email: u.email,
              })).map((account) => (
                <button
                  key={account.email}
                  type="button"
                  disabled={demoLoading !== null}
                  onClick={() => handleDemoLogin(account.email)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-white/10 transition-colors group disabled:opacity-50"
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: "#65ADDD" }}
                  >
                    {account.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-300 group-hover:text-white">
                      {account.name}
                      <span className="ml-1.5 text-xs font-normal text-slate-500">({account.label})</span>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{account.email}</div>
                  </div>
                  {demoLoading === account.email ? (
                    <Loader2 size={14} className="animate-spin text-slate-400" />
                  ) : (
                    <span className="text-xs text-slate-500 group-hover:text-waves-blue transition-colors">Inloggen →</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center mt-6 text-xs text-slate-500">
          © 2026 Waves Network — Alle rechten voorbehouden
        </p>
      </div>
    </div>
  );
}
